import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface Chatbot {
  id: string;
  public_id: string;
  user_id: string;
  name: string;
  is_default: boolean | null;
  status: "active" | "inactive" | "maintenance";
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface ChatbotState {
  chatbot: Chatbot | null;
  isLoading: boolean;
  error: Error | null;
  
  // Actions
  fetchChatbotByPublicId: (publicId: string) => Promise<void>;
  fetchDefaultChatbotByUserId: (userId: string) => Promise<void>;
  reset: () => void;
}

export const useChatbotStore = create<ChatbotState>((set) => ({
  chatbot: null,
  isLoading: false,
  error: null,
  
  fetchChatbotByPublicId: async (publicId: string) => {
    if (!publicId) {
      console.error('fetchChatbotByPublicId: ID du chatbot manquant');
      set({ error: new Error('ID du chatbot requis'), isLoading: false });
      return;
    }
    
    console.log('fetchChatbotByPublicId: Tentative de récupération avec ID =', publicId);
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('chatbots')
        .select('*')
        .eq('public_id', publicId)
        .single();
      
      if (error) {
        console.error('fetchChatbotByPublicId: Erreur Supabase =', error);
        throw error;
      }
      
      if (!data) {
        console.error('fetchChatbotByPublicId: Aucun chatbot trouvé avec ID =', publicId);
        set({ 
          error: new Error(`Aucun chatbot trouvé avec ID: ${publicId}`), 
          isLoading: false 
        });
        return;
      }
      
      console.log('fetchChatbotByPublicId: Chatbot trouvé avec succès, ID =', data.public_id);
      set({ chatbot: data, isLoading: false });
    } catch (error) {
      console.error('fetchChatbotByPublicId: Exception =', error);
      set({ error: error as Error, isLoading: false });
    }
  },
  
  fetchDefaultChatbotByUserId: async (userId: string) => {
    if (!userId) {
      set({ error: new Error('User ID is required to fetch default chatbot'), isLoading: false });
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      // Try to find default chatbot
      const { data, error } = await supabase
        .from('chatbots')
        .select('*')
        .eq('user_id', userId)
        .eq('is_default', true)
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      if (data) {
        set({ chatbot: data, isLoading: false });
        return;
      }
      
      // If no default chatbot, try to find any chatbot
      const { data: anyChatbot, error: anyError } = await supabase
        .from('chatbots')
        .select('*')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();
      
      if (anyError && anyError.code !== 'PGRST116') {
        throw anyError;
      }
      
      if (anyChatbot) {
        set({ chatbot: anyChatbot, isLoading: false });
        return;
      }
      
      // If no chatbot at all, create one
      try {
        const publicId = crypto.randomUUID();
        
        const { data: newChatbot, error: createError } = await supabase
          .from('chatbots')
          .insert([
            {
              user_id: userId,
              name: 'My First Chatbot',
              description: 'Default chatbot',
              status: 'active',
              is_default: true,
              public_id: publicId
            }
          ])
          .select()
          .single();
          
        if (createError) {
          throw createError;
        }
        
        set({ chatbot: newChatbot, isLoading: false });
      } catch (createErr) {
        set({ 
          error: new Error('Failed to create default chatbot. Please refresh or contact support.'),
          isLoading: false
        });
      }
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },
  
  reset: () => {
    set({ chatbot: null, isLoading: false, error: null });
  }
}));